

import { useState, useRef, useCallback } from "react"
import { Upload, X, FileArchive } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

interface ZipUploadProps {
  files: File[]
  onFilesChange: (files: File[]) => void
  label?: string
}

export function ZipUpload({ files, onFilesChange, label = "Arraste um arquivo ZIP ou clique para selecionar" }: ZipUploadProps) {
  const [isDragging, setIsDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const getFirstZipFile = useCallback((candidateFiles: File[]) => {
    return candidateFiles.find(
      (file) =>
        file.type === "application/zip" ||
        file.type === "application/x-zip-compressed" ||
        file.name.toLowerCase().endsWith(".zip")
    )
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)
      const droppedZip = getFirstZipFile(Array.from(e.dataTransfer.files))
      if (droppedZip) {
        onFilesChange([droppedZip])
      }
    },
    [getFirstZipFile, onFilesChange]
  )

  const handleSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedZip = getFirstZipFile(Array.from(e.target.files || []))
      if (selectedZip) {
        onFilesChange([selectedZip])
      }
      if (inputRef.current) inputRef.current.value = ""
    },
    [getFirstZipFile, onFilesChange]
  )

  const removeFile = useCallback(
    (index: number) => {
      onFilesChange(files.filter((_, i) => i !== index))
    },
    [files, onFilesChange]
  )

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <div className="space-y-2">
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={cn(
          "flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed p-4 transition-all",
          isDragging
            ? "border-primary bg-primary/5"
            : "border-border hover:border-primary/40 hover:bg-accent/30"
        )}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") inputRef.current?.click() }}
      >
        <Upload className={cn("h-5 w-5", isDragging ? "text-primary" : "text-muted-foreground")} />
        <p className="text-xs text-muted-foreground text-center">{label}</p>
        <p className="text-[11px] text-muted-foreground/80 text-center">Somente 1 arquivo ZIP por envio.</p>
        <input
          ref={inputRef}
          type="file"
          accept=".zip,application/zip,application/x-zip-compressed"
          className="hidden"
          onChange={handleSelect}
        />
      </div>

      {files.length > 0 && (
        <div className="space-y-1.5">
          {files.map((file, i) => (
            <div
              key={`${file.name}-${i}`}
              className="flex items-center gap-2 rounded-lg bg-accent/50 px-3 py-2"
            >
              <FileArchive className="h-4 w-4 text-primary shrink-0" />
              <span className="flex-1 truncate text-xs text-foreground">{file.name}</span>
              <span className="text-xs text-muted-foreground shrink-0">{formatSize(file.size)}</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                onClick={(e) => {
                  e.stopPropagation()
                  removeFile(i)
                }}
              >
                <X className="h-3 w-3" />
                <span className="sr-only">Remover arquivo</span>
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
